import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import { jobPortalApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { PageSpinner } from '@/components/ui/Feedback'
import { StatusPill, type Tone } from '@/components/ui/StatusPill'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { JobPortalStatus } from '@/types/api'

import { PhoneCodeEntry } from '@/features/setup/PhoneCodeEntry'

import { tabHref } from './shared'

/**
 * The hand-off relays this page adds to the shared live panel's click and
 * type: a press-and-hold and a drag. Called here until `api/endpoints.ts`
 * (another owner's file) lists them.
 */
const handoffExtras = {
  hold: (key: string, x: number, y: number, durationMs: number) =>
    api.post<{ message: string }>(`/job-portals/${encodeURIComponent(key)}/handoff/hold`, { x, y, durationMs }),
  drag: (key: string, from: { x: number; y: number }, to: { x: number; y: number }, durationMs: number) =>
    api.post<{ message: string }>(`/job-portals/${encodeURIComponent(key)}/handoff/drag`, {
      fromX: from.x,
      fromY: from.y,
      toX: to.x,
      toY: to.y,
      durationMs,
    }),
}

/** A press shorter than this, without movement, is a click; longer is a hold. */
const HOLD_AFTER_MS = 350
/** Movement past this many page pixels makes the gesture a drag. */
const DRAG_AFTER_PX = 6
/** What the "Hold" button holds for. */
const HOLD_BUTTON_MS = 3000

/** The portal connection's state, said the one way statuses are said. */
const PORTAL_STATUS: Record<JobPortalStatus, { label: string; tone: Tone; live?: boolean }> = {
  not_connected: { label: 'Not connected', tone: 'neutral' },
  connecting: { label: 'Connecting', tone: 'info', live: true },
  needs_you: { label: 'Needs you', tone: 'warning' },
  connected: { label: 'Connected', tone: 'success' },
  error: { label: 'Error', tone: 'danger' },
}

/**
 * Where the user takes over one step of connecting a job portal — a CAPTCHA,
 * a "confirm you are human" screen, a code texted to their phone. The portal's
 * own page is shown live; when the step is done the connection carries on by itself.
 */
export function PortalHandoffPage() {
  const { key = '' } = useParams()
  const queryClient = useQueryClient()
  // Shares the Job Portals tab's list, and follows the connection while this page is open.
  const portals = useQuery({ queryKey: ['job-portals'], queryFn: jobPortalApi.list, refetchInterval: 4000 })
  const portal = portals.data?.find((entry) => entry.key === key) ?? null
  const done = useMutation({
    mutationFn: () => jobPortalApi.handoffDone(key),
    onSuccess: () => {
      toast.success('Thanks — carrying on')
      queryClient.invalidateQueries({ queryKey: ['job-portals'] })
    },
    onError: () => toast.error('That page is no longer waiting'),
  })

  if (portals.isLoading) return <PageSpinner label="Loading" />
  const name = portal?.name ?? 'this job portal'
  const status = portal ? PORTAL_STATUS[portal.status] : null
  const description =
    portal?.status === 'needs_you' && portal.lastError
      ? portal.lastError
      : `We are setting up your account on ${name}. Some steps only you may do.`

  return (
    <div className="min-w-0">
      {/* Compact and sticky under the app bar: back, the portal, its state. */}
      <header className="sticky top-[calc(var(--safe-top)_+_56px)] z-20 -mx-4 -mt-5 mb-4 border-b border-line bg-surface/95 backdrop-blur-md sm:-mx-6 sm:-mt-6 lg:-mx-8">
        <div className="flex h-12 items-center gap-1 px-1.5 sm:px-3 md:h-14 md:gap-2 lg:px-5">
          <Link
            to={tabHref('portals')}
            aria-label="Back to Job Portals"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-soft transition-colors hover:bg-surface-muted hover:text-ink"
          >
            <BackIcon />
          </Link>
          <div className="min-w-0 flex-1">
            <p className="label-eyebrow hidden md:block">Job portals</p>
            <h1 className="truncate text-[13.5px] font-semibold leading-tight tracking-tight text-ink md:text-[15px]">
              Finish a step on {name}
            </h1>
            <p className="truncate text-[12px] leading-tight text-ink-muted md:hidden">{description}</p>
          </div>
          {status && (
            <StatusPill tone={status.tone} live={status.live}>
              {status.label}
            </StatusPill>
          )}
          <Link
            to={tabHref('portals')}
            className="hidden h-9 items-center rounded-field border border-line bg-surface px-3 text-[13px] font-medium text-ink transition-colors hover:bg-surface-muted md:inline-flex"
          >
            Back to Job Portals
          </Link>
        </div>
      </header>

      <div className="mx-auto w-full max-w-[920px] space-y-4">
        <p className="hidden text-[13.5px] leading-relaxed text-ink-muted md:block">{description}</p>

        <PortalLivePanel
          portalKey={key}
          name={name}
          actions={
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 pt-1">
              <p className="min-w-0 flex-1 text-[12px] leading-snug text-ink-muted">
                When the check is passed the set-up continues by itself. If it does not notice, tell it.
              </p>
              <Button size="sm" variant="secondary" onClick={() => done.mutate()} loading={done.isPending} className="h-11 sm:h-9">
                I have finished this step
              </Button>
            </div>
          }
          idle={
            <section className="card px-4 py-6 text-[14px] text-ink-muted sm:px-5">
              {portal?.status === 'connected'
                ? `${name} is connected. Nothing is waiting for you.`
                : portal?.status === 'connecting'
                  ? `Carrying on with ${name}. If another step needs you it will show here.`
                  : portal?.status === 'needs_you'
                    ? `Nothing on ${name} is open right now. ${portal.lastError ? '' : 'Press Try again on the Job Portals tab when you are ready.'}`
                    : `Nothing on ${name} is waiting for you right now.`}
            </section>
          }
        />
      </div>
    </div>
  )
}

interface Gesture {
  x: number
  y: number
  clientX: number
  clientY: number
  startedAt: number
}

/**
 * The parked page, live, with every gesture a human check may ask for — a
 * click, a press-and-hold, a drag (slider checks) and short typing — relayed
 * onto the real browser as the person made it. Nothing here solves a check;
 * it only carries the person's own pointer and keys across.
 *
 * Full-bleed on phones: the picture goes edge to edge, the controls keep a
 * 16px gutter. Points are mapped through the rendered size, so a finger on a
 * scaled-down page still lands where it was put.
 */
export function PortalLivePanel({
  portalKey,
  name,
  actions,
  idle,
}: {
  portalKey: string
  name: string
  actions?: ReactNode
  idle?: ReactNode
}) {
  const sourceKey = ['portal-handoff', portalKey] as const
  const live = useQuery({
    queryKey: [...sourceKey, 'live'],
    queryFn: () => jobPortalApi.handoff(portalKey),
    refetchInterval: 2000,
    retry: false,
  })
  const waiting = Boolean(live.data?.waiting)
  const shot = useQuery({
    queryKey: [...sourceKey, 'live-shot'],
    queryFn: () => jobPortalApi.handoffScreenshot(portalKey),
    enabled: waiting,
    refetchInterval: 1500,
    retry: false,
  })
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!shot.data) return
    const objectUrl = URL.createObjectURL(shot.data)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [shot.data])

  const [typed, setTyped] = useState('')
  const [lastPoint, setLastPoint] = useState<{ x: number; y: number } | null>(null)
  const gesture = useRef<Gesture | null>(null)
  const [holding, setHolding] = useState(false)

  // The run may have just moved on; the poll hides us. No gesture is retried on its own.
  const click = useMutation({ mutationFn: ({ x, y }: { x: number; y: number }) => jobPortalApi.handoffClick(portalKey, x, y), onError: () => undefined })
  const hold = useMutation({
    mutationFn: ({ x, y, ms }: { x: number; y: number; ms: number }) => handoffExtras.hold(portalKey, x, y, ms),
    onError: () => undefined,
    onSettled: () => setHolding(false),
  })
  const drag = useMutation({
    mutationFn: ({ from, to, ms }: { from: { x: number; y: number }; to: { x: number; y: number }; ms: number }) =>
      handoffExtras.drag(portalKey, from, to, ms),
    onError: () => undefined,
  })
  const send = useMutation({
    mutationFn: () => jobPortalApi.handoffType(portalKey, typed),
    onSuccess: () => setTyped(''),
  })
  if (!waiting) return <>{idle}</>

  const width = live.data?.width ?? 1366
  const height = live.data?.height ?? Math.round((width * 9) / 16)
  /** The pointer's place on the real page: the picture is the viewport, scaled to fit. */
  const pointOf = (event: PointerEvent<HTMLImageElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const scaleX = width / rect.width
    const scaleY = height / rect.height
    return {
      x: Math.max(0, (event.clientX - rect.left) * scaleX),
      y: Math.max(0, (event.clientY - rect.top) * scaleY),
      clientX: event.clientX,
      clientY: event.clientY,
    }
  }
  const onPointerDown = (event: PointerEvent<HTMLImageElement>) => {
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    gesture.current = { ...pointOf(event), startedAt: Date.now() }
    setHolding(true)
  }
  const onPointerUp = (event: PointerEvent<HTMLImageElement>) => {
    const started = gesture.current
    gesture.current = null
    setHolding(false)
    if (!started) return
    const ended = pointOf(event)
    const heldMs = Date.now() - started.startedAt
    const moved = Math.hypot(ended.clientX - started.clientX, ended.clientY - started.clientY)
    setLastPoint({ x: Math.round(started.x), y: Math.round(started.y) })
    if (moved > DRAG_AFTER_PX) {
      drag.mutate({ from: { x: started.x, y: started.y }, to: { x: ended.x, y: ended.y }, ms: Math.max(100, Math.min(heldMs, 10_000)) })
      return
    }
    if (heldMs >= HOLD_AFTER_MS) {
      hold.mutate({ x: started.x, y: started.y, ms: Math.max(100, Math.min(heldMs, 15_000)) })
      return
    }
    click.mutate({ x: started.x, y: started.y })
  }
  const onPointerCancel = () => {
    gesture.current = null
    setHolding(false)
  }

  const statusLine = drag.isPending
    ? 'Dragging…'
    : hold.isPending
      ? 'Holding…'
      : click.isPending
        ? 'Sending the tap…'
        : lastPoint
          ? 'For a "press and hold" check, keep the mouse button down on the picture; for a slider, drag it.'
          : 'Click a spot first to enable the hold button.'

  return (
    <section className="-mx-4 border-y border-warning/30 bg-warning/[0.06] py-3 sm:mx-0 sm:rounded-card sm:border sm:px-4 sm:py-4" aria-live="polite">
      <div className="space-y-2.5">
        <div className="flex items-baseline justify-between gap-3 px-4 sm:px-0">
          <h2 className="min-w-0 truncate text-[14.5px] font-semibold text-ink">{name}, live</h2>
          {typeof live.data?.waitedSeconds === 'number' && (
            <span className="num shrink-0 text-[12px] text-ink-muted">{Math.round(live.data.waitedSeconds)}s</span>
          )}
        </div>
        {live.data?.reason === 'sms' ? (
          <div className="space-y-2 px-4 sm:px-0">
            <p className="text-[12.5px] text-ink-muted">
              {name} wants to confirm your phone. Type the code here; the page below shows what happens.
            </p>
            <PhoneCodeEntry portalKey={portalKey} name={name} />
          </div>
        ) : (
          <p className="truncate px-4 text-[12.5px] text-ink-muted sm:px-0">
            Click, press and hold, or drag directly on the page. We never solve these checks for you.
          </p>
        )}

        {url ? (
          <img
            src={url}
            alt={`${name}'s page, live — click, hold or drag to interact`}
            draggable={false}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onContextMenu={(event) => event.preventDefault()}
            className={cn(
              'block h-auto w-full touch-none select-none border-y bg-surface sm:rounded-field sm:border',
              holding ? 'cursor-grabbing border-warning' : 'cursor-crosshair border-line',
            )}
          />
        ) : (
          <div
            className="flex w-full items-center justify-center border-y border-line bg-surface text-[13px] text-ink-muted sm:rounded-field sm:border"
            style={{ aspectRatio: `${width} / ${height}` }}
          >
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
              Loading the live page…
            </span>
          </div>
        )}

        {/* The relay controls, one compact toolbar: status, hold, type, done. */}
        <div className="flex flex-col gap-2 px-4 sm:px-0">
          <p className="text-[12px] leading-snug text-ink-muted">{statusLine}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={!lastPoint}
              loading={hold.isPending}
              onClick={() => lastPoint && hold.mutate({ x: lastPoint.x, y: lastPoint.y, ms: HOLD_BUTTON_MS })}
              title={lastPoint ? `Press and hold for ${HOLD_BUTTON_MS / 1000} seconds where you last clicked` : 'Click a spot on the page first'}
              className="h-11 shrink-0 sm:h-9"
            >
              Hold <span className="num">{HOLD_BUTTON_MS / 1000}s</span> where I clicked
            </Button>
            <form
              className="flex min-w-0 flex-1 basis-[240px] gap-2"
              onSubmit={(event) => {
                event.preventDefault()
                if (typed.trim()) send.mutate()
              }}
            >
              <input
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                maxLength={200}
                aria-label="Text to type into the page"
                placeholder="Need to type something? Click its field above first, then send."
                className="field-box h-11 min-w-0 flex-1 py-0 text-[16px] sm:h-9"
              />
              <Button type="submit" disabled={!typed.trim()} loading={send.isPending} className="h-11 shrink-0 sm:h-9 sm:px-3.5 sm:text-[13px]">
                Type it
              </Button>
            </form>
          </div>
          {actions}
        </div>
      </div>
    </section>
  )
}

function BackIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M12.5 4.5 7 10l5.5 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
